import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Upload, Eye, EyeOff, Trash2, Star, BarChart2, Package, Search, RefreshCw, X, CheckCircle, AlertCircle, Loader2, TrendingUp, Clock, Zap, Building2, FileBox, Plus, Edit3, ArrowLeft } from 'lucide-react';

import {
  fetchModels, updateModel, deleteModel, toggleModelVisibility,
  setPrimaryModel, uploadModelFile, fetchModelMetricsSummary,
  fetchPropertiesForAssignment
} from '../../../services/modelManagementService';
import Icon from '../../../components/AppIcon';




// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatBytes = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024)?.toFixed(1)} KB`;
  return `${(b / 1048576)?.toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const FILE_TYPE_COLORS = {
  glb: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  gltf: { bg: 'bg-teal-100', text: 'text-teal-700', dot: 'bg-teal-500' },
  obj: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
};

const VISIBILITY_CONFIG = {
  visible: { label: 'Visible', icon: Eye, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  hidden: { label: 'Masqué', icon: EyeOff, color: 'text-slate-500', bg: 'bg-slate-100' },
  draft: { label: 'Brouillon', icon: Edit3, color: 'text-amber-600', bg: 'bg-amber-50' },
};

// ─── Upload Modal ─────────────────────────────────────────────────────────────
const UploadModal = ({ properties, onClose, onSuccess, uploadedBy }) => {
  const [file, setFile] = useState(null);
  const [propertyId, setPropertyId] = useState('');
  const [modelName, setModelName] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const dropRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    const ext = f?.name?.split('.')?.pop()?.toLowerCase();
    if (!['glb', 'gltf', 'obj']?.includes(ext)) {
      setError('Format non supporté. Utilisez .glb, .gltf ou .obj');
      return;
    }
    setFile(f);
    setModelName(f?.name?.replace(/\.[^/.]+$/, ''));
    setError(null);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const f = e?.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!file || !propertyId || !modelName?.trim()) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await uploadModelFile(file, propertyId, modelName?.trim(), uploadedBy, setProgress);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
              <Upload size={18} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>Ajouter un modèle 3D</h3>
              <p className="text-xs text-slate-500">GLB, GLTF ou OBJ</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Drop zone */}
          <div
            ref={dropRef}
            onDrop={handleDrop}
            onDragOver={(e) => e?.preventDefault()}
            onClick={() => document.getElementById('model-file-input')?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
              file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-400 bg-slate-50'
            }`}
          >
            <input
              id="model-file-input"
              type="file"
              accept=".glb,.gltf,.obj"
              className="hidden"
              onChange={(e) => handleFile(e?.target?.files?.[0])}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileBox size={24} className="text-emerald-600" />
                <div className="text-left">
                  <p className="text-sm font-medium text-emerald-700">{file?.name}</p>
                  <p className="text-xs text-emerald-600">{formatBytes(file?.size)}</p>
                </div>
              </div>
            ) : (
              <div>
                <Package size={32} className="text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600 font-medium">Glissez votre fichier ici</p>
                <p className="text-xs text-slate-400 mt-1">ou cliquez pour parcourir</p>
                <p className="text-xs text-slate-400 mt-2">.glb · .gltf · .obj — max 500 MB</p>
              </div>
            )}
          </div>

          {/* Property selector */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Propriété *</label>
            <select
              value={propertyId}
              onChange={(e) => setPropertyId(e?.target?.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20"
              required
            >
              <option value="">Sélectionner une propriété</option>
              {properties?.map((p) => (
                <option key={p?.id} value={p?.id}>{p?.title} — {p?.commune}</option>
              ))}
            </select>
          </div>

          {/* Model name */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Nom du modèle *</label>
            <input
              type="text"
              value={modelName}
              onChange={(e) => setModelName(e?.target?.value)}
              placeholder="Ex: Vue principale, Intérieur salon..."
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
              required
            />
          </div>

          {/* Progress */}
          {uploading && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-600">Téléversement en cours...</span>
                <span className="text-xs font-medium text-slate-900">{progress}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-900 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-700 text-sm">
              <AlertCircle size={15} />
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={uploading || !file}
              className="flex-1 px-4 py-2.5 bg-slate-900 rounded-xl text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {uploading ? 'Téléversement...' : 'Téléverser'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Edit Modal ───────────────────────────────────────────────────────────────
const EditModal = ({ model, properties, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: model?.name || '',
    description: model?.description || '',
    visibility: model?.visibility || 'visible',
    propertyId: model?.property_id || '',
    displayOrder: model?.display_order || 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await updateModel(model?.id, {
        name: form?.name,
        description: form?.description,
        visibility: form?.visibility,
        propertyId: form?.propertyId,
        displayOrder: parseInt(form?.displayOrder) || 0,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
              <Edit3 size={18} className="text-white" />
            </div>
            <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>Modifier le modèle</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Nom *</label>
            <input
              type="text"
              value={form?.name}
              onChange={(e) => setForm(f => ({ ...f, name: e?.target?.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Description</label>
            <textarea
              value={form?.description}
              onChange={(e) => setForm(f => ({ ...f, description: e?.target?.value }))}
              rows={2}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Visibilité</label>
              <select
                value={form?.visibility}
                onChange={(e) => setForm(f => ({ ...f, visibility: e?.target?.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20"
              >
                <option value="visible">Visible</option>
                <option value="hidden">Masqué</option>
                <option value="draft">Brouillon</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Ordre d'affichage</label>
              <input
                type="number"
                value={form?.displayOrder}
                onChange={(e) => setForm(f => ({ ...f, displayOrder: e?.target?.value }))}
                min={0}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Propriété</label>
            <select
              value={form?.propertyId}
              onChange={(e) => setForm(f => ({ ...f, propertyId: e?.target?.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20"
            >
              <option value="">Aucune propriété</option>
              {properties?.map((p) => (
                <option key={p?.id} value={p?.id}>{p?.title} — {p?.commune}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-700 text-sm">
              <AlertCircle size={15} /> {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 bg-slate-900 rounded-xl text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Model Row ────────────────────────────────────────────────────────────────
const ModelRow = ({ model, onToggleVisibility, onDelete, onSetPrimary, onEdit, metrics }) => {
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const typeColors = FILE_TYPE_COLORS?.[model?.file_type] || FILE_TYPE_COLORS?.glb;
  const visConfig = VISIBILITY_CONFIG?.[model?.visibility] || VISIBILITY_CONFIG?.visible;
  const VisIcon = visConfig?.icon;
  const modelMetrics = metrics?.find(m => m?.modelId === model?.id);

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer "${model?.name}" ? Cette action est irréversible.`)) return;
    setDeleting(true);
    try {
      await onDelete(model?.id, model?.storage_path);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      await onToggleVisibility(model?.id, model?.visibility);
    } finally {
      setToggling(false);
    }
  };

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors group">
      {/* Model info */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center flex-shrink-0">
            <Package size={16} className={typeColors?.text} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-slate-900 truncate max-w-[180px]">{model?.name}</p>
              {model?.is_primary && (
                <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                  <Star size={10} /> Principal
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{formatBytes(model?.file_size_bytes)} · {formatDate(model?.created_at)}</p>
          </div>
        </div>
      </td>
      {/* Type */}
      <td className="px-4 py-4">
        <span className={`inline-flex items-center gap-1.5 text-xs font-mono font-bold px-2.5 py-1 rounded-full ${typeColors?.bg} ${typeColors?.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${typeColors?.dot}`} />
          .{model?.file_type?.toUpperCase()}
        </span>
      </td>
      {/* Property */}
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <Building2 size={13} className="text-slate-400 flex-shrink-0" />
          <span className="text-sm text-slate-600 truncate max-w-[140px]">
            {model?.properties?.title || <span className="text-slate-400 italic">Non assigné</span>}
          </span>
        </div>
        {model?.properties?.commune && (
          <p className="text-xs text-slate-400 mt-0.5 ml-5">{model?.properties?.commune}</p>
        )}
      </td>
      {/* Visibility */}
      <td className="px-4 py-4">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${visConfig?.bg} ${visConfig?.color}`}>
          <VisIcon size={11} />
          {visConfig?.label}
        </span>
      </td>
      {/* Metrics */}
      <td className="px-4 py-4">
        {modelMetrics ? (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <Eye size={11} className="text-slate-400" />
              <span className="font-medium">{modelMetrics?.totalViews?.toLocaleString()}</span>
              <span className="text-slate-400">vues</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Clock size={11} className="text-slate-400" />
              <span>{modelMetrics?.avgViewDuration?.toFixed(0)}s moy.</span>
            </div>
          </div>
        ) : (
          <span className="text-xs text-slate-400 italic">Aucune donnée</span>
        )}
      </td>
      {/* Actions */}
      <td className="px-4 py-4">
        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(model)}
            title="Modifier"
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors text-slate-600"
          >
            <Edit3 size={14} />
          </button>
          <button
            onClick={handleToggle}
            disabled={toggling}
            title={model?.visibility === 'visible' ? 'Masquer' : 'Afficher'}
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors text-slate-600 disabled:opacity-50"
          >
            {toggling ? <Loader2 size={14} className="animate-spin" /> : model?.visibility === 'visible' ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          {!model?.is_primary && model?.property_id && (
            <button
              onClick={() => onSetPrimary(model?.id, model?.property_id)}
              title="Définir comme principal"
              className="p-1.5 hover:bg-amber-100 rounded-lg transition-colors text-slate-600 hover:text-amber-600"
            >
              <Star size={14} />
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={deleting}
            title="Supprimer"
            className="p-1.5 hover:bg-red-100 rounded-lg transition-colors text-slate-600 hover:text-red-600 disabled:opacity-50"
          >
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─── Stats Card ───────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color = 'text-slate-900', bgColor = 'bg-slate-100' }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-start gap-4">
    <div className={`w-11 h-11 ${bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
      <Icon size={20} className={color} />
    </div>
    <div>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: 'DM Sans, sans-serif' }}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const ModelAdminDashboard = ({ onBack }) => {
  const [models, setModels] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterProperty, setFilterProperty] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [activeTab, setActiveTab] = useState('models');
  const { user } = (() => {
    try { return require('../../../contexts/AuthContext')?.useAuth(); } catch { return { user: null }; }
  })();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [modelsData, propsData] = await Promise.all([
        fetchModels(),
        fetchPropertiesForAssignment(),
      ]);
      setModels(modelsData);
      setProperties(propsData);
    } catch (err) {
      console.error('Error loading models:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const data = await fetchModelMetricsSummary();
      setMetrics(data);
    } catch (err) {
      console.error('Error loading metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadMetrics();
  }, [loadData, loadMetrics]);

  const handleToggleVisibility = async (modelId, currentVisibility) => {
    await toggleModelVisibility(modelId, currentVisibility);
    await loadData();
  };

  const handleDelete = async (modelId, storagePath) => {
    await deleteModel(modelId, storagePath);
    await loadData();
    await loadMetrics();
  };

  const handleSetPrimary = async (modelId, propertyId) => {
    await setPrimaryModel(modelId, propertyId);
    await loadData();
  };

  // Filtered models
  const filteredModels = models?.filter((m) => {
    const matchSearch = !search || m?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      m?.properties?.title?.toLowerCase()?.includes(search?.toLowerCase());
    const matchVis = filterVisibility === 'all' || m?.visibility === filterVisibility;
    const matchType = filterType === 'all' || m?.file_type === filterType;
    const matchProp = filterProperty === 'all' || m?.property_id === filterProperty;
    return matchSearch && matchVis && matchType && matchProp;
  });

  // Summary stats
  const totalViews = metrics?.reduce((s, m) => s + (m?.totalViews || 0), 0);
  const avgLoadTime = metrics?.length ? (metrics?.reduce((s, m) => s + (m?.avgLoadTime || 0), 0) / metrics?.length)?.toFixed(0) : 0;
  const visibleCount = models?.filter(m => m?.visibility === 'visible')?.length;
  const topModel = [...metrics]?.sort((a, b) => (b?.totalViews || 0) - (a?.totalViews || 0))?.[0];

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <ArrowLeft size={18} className="text-slate-600" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                <Box size={18} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Gestion Modèles 3D</h1>
                <p className="text-xs text-slate-500">Assignation, visibilité et performances</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { loadData(); loadMetrics(); }}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
              title="Actualiser"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              <Plus size={15} />
              Ajouter un modèle
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Package} label="Total modèles" value={models?.length} sub={`${visibleCount} visibles`} bgColor="bg-slate-100" color="text-slate-700" />
          <StatCard icon={Eye} label="Vues totales" value={totalViews?.toLocaleString()} sub="Toutes périodes" bgColor="bg-blue-50" color="text-blue-600" />
          <StatCard icon={Zap} label="Temps de chargement moy." value={`${avgLoadTime}ms`} sub="Moyenne globale" bgColor="bg-amber-50" color="text-amber-600" />
          <StatCard icon={TrendingUp} label="Modèle le plus vu" value={topModel?.model?.name ? topModel?.model?.name?.slice(0, 14) + (topModel?.model?.name?.length > 14 ? '…' : '') : '—'} sub={topModel ? `${topModel?.totalViews} vues` : 'Aucune donnée'} bgColor="bg-emerald-50" color="text-emerald-600" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[
            { id: 'models', label: 'Modèles', icon: Package },
            { id: 'metrics', label: 'Performances', icon: BarChart2 },
          ]?.map(tab => {
            const TabIcon = tab?.icon;
            return (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab?.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <TabIcon size={15} />
                {tab?.label}
              </button>
            );
          })}
        </div>

        {/* Models Tab */}
        {activeTab === 'models' && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            {/* Filters */}
            <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher un modèle ou propriété..."
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                />
              </div>
              <select
                value={filterVisibility}
                onChange={(e) => setFilterVisibility(e?.target?.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
              >
                <option value="all">Toutes visibilités</option>
                <option value="visible">Visible</option>
                <option value="hidden">Masqué</option>
                <option value="draft">Brouillon</option>
              </select>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e?.target?.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
              >
                <option value="all">Tous formats</option>
                <option value="glb">.GLB</option>
                <option value="gltf">.GLTF</option>
                <option value="obj">.OBJ</option>
              </select>
              <select
                value={filterProperty}
                onChange={(e) => setFilterProperty(e?.target?.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/20 max-w-[200px]"
              >
                <option value="all">Toutes propriétés</option>
                {properties?.map(p => (
                  <option key={p?.id} value={p?.id}>{p?.title}</option>
                ))}
              </select>
              <span className="text-xs text-slate-400 ml-auto">{filteredModels?.length} résultat{filteredModels?.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={28} className="animate-spin text-slate-400" />
              </div>
            ) : filteredModels?.length === 0 ? (
              <div className="text-center py-16">
                <Package size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">Aucun modèle trouvé</p>
                <p className="text-slate-400 text-sm mt-1">Ajoutez votre premier modèle 3D</p>
                <button
                  onClick={() => setShowUpload(true)}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors"
                >
                  <Plus size={15} /> Ajouter un modèle
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Modèle</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Format</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Propriété</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Visibilité</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Métriques</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModels?.map((model) => (
                      <ModelRow
                        key={model?.id}
                        model={model}
                        metrics={metrics}
                        onToggleVisibility={handleToggleVisibility}
                        onDelete={handleDelete}
                        onSetPrimary={handleSetPrimary}
                        onEdit={setEditingModel}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Metrics Tab */}
        {activeTab === 'metrics' && (
          <div className="space-y-4">
            {metricsLoading ? (
              <div className="flex items-center justify-center py-16 bg-white rounded-2xl border border-slate-100">
                <Loader2 size={28} className="animate-spin text-slate-400" />
              </div>
            ) : metrics?.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                <BarChart2 size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">Aucune donnée de performance</p>
                <p className="text-slate-400 text-sm mt-1">Les métriques s'accumuleront au fil des visites</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="p-5 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-900">Performance par modèle</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Données agrégées toutes périodes</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Modèle</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Vues</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Visiteurs uniques</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Durée moy.</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Chargement moy.</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Erreurs</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Dernière vue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...metrics]?.sort((a, b) => (b?.totalViews || 0) - (a?.totalViews || 0))?.map((m) => {
                        const typeColors = FILE_TYPE_COLORS?.[m?.model?.file_type] || FILE_TYPE_COLORS?.glb;
                        return (
                          <tr key={m?.modelId} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Package size={13} className={typeColors?.text} />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-900">{m?.model?.name || 'Modèle inconnu'}</p>
                                  <span className={`text-xs font-mono ${typeColors?.text}`}>.{m?.model?.file_type?.toUpperCase()}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <span className="text-sm font-semibold text-slate-900">{(m?.totalViews || 0)?.toLocaleString()}</span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <span className="text-sm text-slate-600">{(m?.totalUniqueViewers || 0)?.toLocaleString()}</span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <span className="text-sm text-slate-600">{(m?.avgViewDuration || 0)?.toFixed(0)}s</span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <span className={`text-sm font-medium ${(m?.avgLoadTime || 0) > 3000 ? 'text-red-600' : (m?.avgLoadTime || 0) > 1500 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {(m?.avgLoadTime || 0)?.toFixed(0)}ms
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <span className={`text-sm ${(m?.totalErrors || 0) > 0 ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                                {m?.totalErrors || 0}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span className="text-xs text-slate-500">{formatDate(m?.lastViewedAt)}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Modals */}
      {showUpload && (
        <UploadModal
          properties={properties}
          uploadedBy={user?.id}
          onClose={() => setShowUpload(false)}
          onSuccess={() => { loadData(); loadMetrics(); }}
        />
      )}
      {editingModel && (
        <EditModal
          model={editingModel}
          properties={properties}
          onClose={() => setEditingModel(null)}
          onSuccess={() => { loadData(); setEditingModel(null); }}
        />
      )}
    </div>
  );
};

export default ModelAdminDashboard;
